import { useEffect, useState } from 'react';

import { themeGet } from '@styled-system/theme-get';
import * as T from 'prop-types';
import styled from 'styled-components/macro';

import { Badge, Button, Flex } from '@olist/united';

const SwitcherArea = styled(Flex)`
  border: 1px solid ${themeGet('colors.foundation.shadeLight')};
  border-radius: 50px;
  margin: 0 auto;
  width: max-content;
`;

const TagContainer = styled.div`
  position: relative;
`;

const IdleButton = styled(Button)`
  background-color: ${themeGet('colors.foundation.shadeWhite')};
  border: none;
  border-radius: 50px;
  margin: ${themeGet('space.1')}px;
  text-transform: lowercase;

  &:focus {
    background-color: ${themeGet('colors.foundation.shadeWhite')};
  }

  ${({ isActive, theme }) =>
    isActive &&
    `
    background-color: ${theme.olist.color.primary.strong};
    color: ${theme.olist.color.neutral.softest};
    &:focus {
      background-color: ${theme.olist.color.primary.strong};
      color: ${theme.olist.color.neutral.softest};
    }
    &:hover {
      background-color: ${theme.olist.color.primary.strong};
      color: ${theme.olist.color.neutral.softest};
    }
  `}
`;

export const CustomBadge = styled(Badge)`
  background-color: ${themeGet('colors.foundation.successDark')};
  border-radius: 4px;
  font-weight: 600;
  height: 26px;
  justify-content: space-around;
  left: -10%;
  position: absolute;
  top: -44px;
  width: 102px;

  &:before {
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${themeGet('colors.foundation.successDark')};
    content: '';
    left: 45%;
    position: absolute;
    top: 100%;
    width: 0;
  }

  ${({ isActive, theme }) =>
    !isActive &&
    `
      background: ${theme.olist.color.accent.softer};
      color: ${theme.olist.color.accent.base};

      &:before {
        border-top: 8px solid ${theme.olist.color.accent.softer};
      }
  `}
`;

const ButtonSwitcher = ({ initialBtn, options }) => {
  const [activeID, setActiveID] = useState(null);

  useEffect(() => {
    if (activeID) {
      return;
    }
    if (initialBtn) {
      setActiveID(initialBtn);
      return;
    }
    if (options && options[0] && options[0].label) {
      setActiveID(options[0].label);
    }
  }, [initialBtn, activeID, options]);

  if (options && options.length > 0) {
    const buttons = options.map((item) => {
      const { label, onClick } = item;
      const isActive = activeID === label;
      const inactiveTestId = `tag-btn-inactive-${label.replace(/ /g, '-')}`;
      const activeTestId = `tag-btn-active-${label.replace(/ /g, '-')}`;

      return (
        <TagContainer key={`_${label}}`}>
          <IdleButton
            id="periodicity-btn"
            data-testid={isActive ? activeTestId : inactiveTestId}
            border="transparent"
            isActive={isActive}
            variation="secondary"
            onClick={() => {
              setActiveID(label);
              if (onClick) {
                onClick();
              }
            }}
          >
            {label}
          </IdleButton>
          {item.maxDiscount && <CustomBadge>{item.maxDiscount}</CustomBadge>}
        </TagContainer>
      );
    });
    return <SwitcherArea> {buttons}</SwitcherArea>;
  }

  return null;
};

ButtonSwitcher.defaultProps = {
  initialBtn: null,
  options: [],
};

ButtonSwitcher.propTypes = {
  initialBtn: T.string,
  options: T.arrayOf(
    T.shape({
      label: T.string.isRequired,
      onClick: T.func,
      maxDiscount: T.string,
    })
  ),
};

export default ButtonSwitcher;
